<template>
    <hox-modal class="publish-modal" @close="close">
        <template #header>
            <Icon type="logo-facebook" size="43" />
            <span>{{ title }}</span>
        </template>

        <FacebookCreativeManagerSettings
            v-if="isFacebook"
            :deliverables="deliverables"
            :action="action"
            @publishHandlerReady="onPublishHandlerReady"
        />

        <template #footer>
            <Button class="publish-modal__button" :disabled="loading" @click="close">Cancel</Button>
            <Button
                class="publish-modal__button"
                :disabled="!readyToSubmit"
                :loading="loading"
                type="primary"
                @click="submit"
            >
                {{ actionText }}
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import { PLATFORM } from "hoxton-shared/enums/exports";
import { USER_ACTIONS } from "hoxton-shared/enums/user";

import type { ConstructedDeliverable } from "@/types/types";

import HoxModal from "@/components/Modal/Modal/Modal.vue";
import FacebookCreativeManagerSettings from "@/components/Campaign/Publish/Facebook/FacebookCreativeManagerSettings.vue";

import { PlatformLabel } from "@/enums/platforms";
import { userCan } from "@/services/Auth";

export default defineComponent({
    components: {
        FacebookCreativeManagerSettings,
        HoxModal
    },
    props: {
        action: {
            required: true,
            type: String as PropType<"publish" | "unpublish" | "rebuild">
        },

        deliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },

        platform: {
            required: true,
            type: String as PropType<keyof typeof PlatformLabel>
        }
    },

    emits: ["close"],

    data() {
        return {
            publishHandler: null as unknown as () => Promise<void> | null,
            loading: false
        };
    },

    computed: {
        actionText() {
            if (this.action === "unpublish") {
                return "Unpublish";
            }
            if (this.action === "rebuild") {
                return "Reset";
            }
            return "Publish";
        },

        userCanPublishDeliverable() {
            return userCan(USER_ACTIONS.canPublishDeliverableToAdServer, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        isFacebook() {
            return this.platform === PLATFORM.FACEBOOK;
        },

        readyToSubmit() {
            return typeof this.publishHandler === "function";
        },

        title() {
            const platformLabel = PlatformLabel[this.platform];

            if (this.action === "publish") {
                return `${this.actionText} to ${platformLabel}`;
            }
            if (this.action === "unpublish") {
                return `${this.actionText} from ${platformLabel}`;
            }
            if (this.action === "rebuild") {
                return `${this.actionText} ${platformLabel}`;
            }

            return this.action;
        }
    },

    watch: {
        userCanPublishDeliverable() {
            if (!this.userCanPublishDeliverable) {
                this.$snackbar.warning("You do not have access to publish deliverables");
                this.close();
            }
        }
    },

    methods: {
        close() {
            this.$emit("close");
        },

        onPublishHandlerReady(publishHandler: () => Promise<void>) {
            this.publishHandler = publishHandler;
        },

        async submit() {
            this.loading = true;
            if (this.readyToSubmit) {
                await this.publishHandler();
                this.loading = false;
                this.close();
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.publish-modal__button {
    margin: 0 0 0 $spacing-small;
}
</style>
